import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Award, X, Plus, ExternalLink, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/utils/datetime";

/** Certificates management list + issue dialog + revoke. */
export default function Certificates() {
    const { t, i18n } = useTranslation();
    const { activeMembership } = useAuth();
    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [issueOpen, setIssueOpen] = useState(false);

    const load = async () => {
        setLoading(true); setErr(null);
        try { const { data } = await api.get("/certificates"); setItems(data.items || []); }
        catch (e: any) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const revoke = async (id: string) => {
        const reason = window.prompt(t("cert.revoke_reason_prompt") as string);
        if (!reason) return;
        try { await api.post(`/certificates/${id}/revoke`, { reason }); await load(); }
        catch (e: any) { alert(formatApiError(e)); }
    };

    return (
        <div className="space-y-4" data-testid="certs-page">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold">{t("cert.title")}</h1>
                <button onClick={() => setIssueOpen(true)} data-testid="cert-issue-open" className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                    <Plus size={16} />{t("cert.issue")}
                </button>
            </div>
            {loading && <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div>}
            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}
            {!loading && (
                <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                    <table className="w-full text-sm">
                        <thead style={{ background: "var(--input)" }}>
                            <tr>
                                <th className="text-left px-3 py-2">{t("cert.number")}</th>
                                <th className="text-left px-3 py-2">{t("cert.person")}</th>
                                <th className="text-left px-3 py-2">{t("cert.program")}</th>
                                <th className="text-left px-3 py-2">{t("cert.issued_at")}</th>
                                <th className="text-left px-3 py-2">{t("common.status")}</th>
                                <th className="text-left px-3 py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => (
                                <tr key={c.id} data-testid={`cert-row-${c.cert_number}`} className="border-t" style={{ borderColor: "var(--border)" }}>
                                    <td className="px-3 py-2 font-mono">{c.cert_number}</td>
                                    <td className="px-3 py-2">{c.person_name}</td>
                                    <td className="px-3 py-2">{c.program_name}</td>
                                    <td className="px-3 py-2">{fmtDate(c.issued_at)}</td>
                                    <td className="px-3 py-2">
                                        <span className="text-xs rounded-full px-2 py-0.5" style={{ background: c.status === "issued" ? "var(--success-bg)" : "var(--error-bg)", color: c.status === "issued" ? "var(--success-fg)" : "var(--error-fg)" }}>{t(`verify.status.${c.status}`)}</span>
                                    </td>
                                    <td className="px-3 py-2 flex items-center gap-3">
                                        <a href={`/verify/${c.cert_number}`} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1" style={{ color: "var(--primary)" }} data-testid={`cert-verify-link-${c.cert_number}`}><ExternalLink size={12} />{t("cert.verify_link")}</a>
                                        {c.status === "issued" && <button onClick={() => revoke(c.id)} data-testid={`cert-revoke-${c.cert_number}`} className="text-xs" style={{ color: "var(--error-fg)" }}>{t("cert.revoke")}</button>}
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center" style={{ color: "var(--text-muted)" }}>{t("common.empty")}</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
            {issueOpen && <IssueDialog onClose={() => setIssueOpen(false)} onIssued={() => { setIssueOpen(false); load(); }} />}
        </div>
    );
}

function IssueDialog({ onClose, onIssued }: { onClose: () => void; onIssued: () => void }) {
    const { t } = useTranslation();
    const [personId, setPersonId] = useState("");
    const [programId, setProgramId] = useState("");
    const [override, setOverride] = useState("");
    const [completion, setCompletion] = useState<any>(null);
    const [people, setPeople] = useState<any[]>([]);
    const [programs, setPrograms] = useState<any[]>([]);
    const [err, setErr] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [pp, prg] = await Promise.all([
                    api.get("/people", { params: { person_types: "student", limit: 200 } }),
                    api.get("/programs"),
                ]);
                setPeople(pp.data.items || []);
                setPrograms(prg.data.items || []);
            } catch { /* empty */ }
        })();
    }, []);

    useEffect(() => {
        setCompletion(null);
        if (!personId || !programId) return;
        (async () => {
            try { const { data } = await api.get(`/academics/completion/${programId}/${personId}`); setCompletion(data); }
            catch { setCompletion(null); }
        })();
    }, [personId, programId]);

    const submit = async () => {
        if (!personId || !programId) return;
        setBusy(true); setErr(null);
        try {
            await api.post("/certificates", { person_id: personId, program_id: programId, override_reason: override.trim() || undefined });
            onIssued();
        } catch (e: any) {
            const d = e?.response?.data?.detail;
            if (d?.error === "completion_not_met" && !override.trim()) {
                setErr(t("cert.completion_not_met") as string);
                setCompletion(d.completion);
            } else {
                setErr(formatApiError(e));
            }
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.6)" }}>
            <div className="w-full max-w-md rounded-xl border p-5 space-y-3" style={{ background: "var(--card)", borderColor: "var(--border)" }} data-testid="cert-issue-dialog">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Award size={18} /><h2 className="text-lg font-semibold">{t("cert.issue")}</h2></div>
                    <button onClick={onClose} data-testid="cert-issue-close"><X size={18} /></button>
                </div>
                <label className="block text-sm">{t("cert.person")}
                    <select data-testid="cert-issue-person" value={personId} onChange={(e) => setPersonId(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2 text-sm" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }}>
                        <option value="">—</option>
                        {people.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
                    </select>
                </label>
                <label className="block text-sm">{t("cert.program")}
                    <select data-testid="cert-issue-program" value={programId} onChange={(e) => setProgramId(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2 text-sm" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }}>
                        <option value="">—</option>
                        {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </label>
                {completion && (
                    <div className="rounded-md p-2.5 text-xs" style={{ background: completion.meets_requirements ? "var(--success-bg)" : "var(--warning-bg)", color: completion.meets_requirements ? "var(--success-fg)" : "var(--warning-fg)" }} data-testid="cert-completion-info">
                        <div>{t("cert.attendance")}: {completion.attendance_pct}% / {completion.rule.min_attendance_pct}%</div>
                        {completion.rule.min_avg_grade != null && <div>{t("cert.avg_grade")}: {completion.avg_grade ?? "—"} / {completion.rule.min_avg_grade}</div>}
                        <div className="mt-1 font-medium">{completion.meets_requirements ? t("cert.meets") : t("cert.not_meets")}</div>
                    </div>
                )}
                {completion && !completion.meets_requirements && (
                    <label className="block text-sm">{t("cert.override_reason")}
                        <input data-testid="cert-override" value={override} onChange={(e) => setOverride(e.target.value)} className="mt-1 w-full rounded-md border px-3 py-2 text-sm" style={{ background: "var(--input)", borderColor: "var(--border)", color: "var(--text)" }} />
                    </label>
                )}
                {err && <div className="text-xs" style={{ color: "var(--error-fg)" }}>{err}</div>}
                <div className="flex justify-end gap-2 pt-2">
                    <button onClick={onClose} className="rounded-md px-3 py-2 text-sm" style={{ color: "var(--text-muted)" }}>{t("common.cancel")}</button>
                    <button disabled={!personId || !programId || busy} data-testid="cert-issue-submit" onClick={submit} className="rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", opacity: (!personId || !programId || busy) ? 0.6 : 1 }}>{t("cert.issue")}</button>
                </div>
            </div>
        </div>
    );
}
